import { useEffect, useRef, useState } from 'react';

import { ButtonVariant, TextField } from '@cfa/react-components';

import { PathwayModal } from '@/components/popups/PathwayModal';

interface InputDialogProps {
  allowBlankInput?: boolean;
  bodyText?: string;
  headerText: string;
  isOpen: boolean;
  keyDownEnter?: boolean;
  onClose: () => void;
  primaryButtonDisabled?: boolean;
  primaryButtonHandler?: (value: string) => void;
  primaryButtonText: string;
  primaryButtonVariant?: ButtonVariant;
  secondaryButtonHandler?: () => void;
  secondaryButtonText: string;
  textInputDefaultValue?: string;
  textInputMaxLength?: number;
  textInputPlaceholder?: string;
}

const InputDialog: React.FC<InputDialogProps> = ({
  allowBlankInput,
  bodyText,
  headerText,
  isOpen,
  onClose,
  primaryButtonDisabled,
  primaryButtonHandler,
  primaryButtonText,
  primaryButtonVariant,
  keyDownEnter,
  secondaryButtonHandler,
  secondaryButtonText,
  textInputDefaultValue,
  textInputMaxLength,
  textInputPlaceholder,
}) => {
  const [textInputValue, setTextInputValue] = useState<string>();
  const inputRef = useRef<HTMLInputElement>(null);

  const clampedDefaultValue =
    textInputMaxLength !== undefined && textInputDefaultValue !== undefined
      ? textInputDefaultValue.slice(0, textInputMaxLength)
      : textInputDefaultValue;

  const isDisabled =
    !!primaryButtonDisabled ||
    (!allowBlankInput &&
      (!textInputValue || textInputValue === clampedDefaultValue));

  useEffect(() => {
    if (!isOpen) return;
    const timer = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 150);
    return () => window.clearTimeout(timer);
  }, [isOpen]);

  const onSecondaryClick = () => {
    setTextInputValue(clampedDefaultValue);
    secondaryButtonHandler?.();
  };

  return (
    <PathwayModal
      isOpen={isOpen}
      onClose={onClose}
      className="2xl:max-w-[610px]!"
    >
      <PathwayModal.Header>
        <PathwayModal.Title>{headerText}</PathwayModal.Title>
      </PathwayModal.Header>
      <PathwayModal.Body>
        {bodyText && (
          <div
            dangerouslySetInnerHTML={{
              __html: bodyText as string | TrustedHTML,
            }}
          />
        )}
        <TextField
          ref={inputRef}
          data-testid="ModalInput"
          defaultValue={clampedDefaultValue}
          fullWidth={true}
          maxLength={textInputMaxLength}
          onChange={e => setTextInputValue(e.target.value)}
          onKeyDown={e => {
            if (keyDownEnter && e.key === 'Enter' && !isDisabled) {
              primaryButtonHandler?.(
                textInputValue ?? clampedDefaultValue ?? '',
              );
            }
          }}
          placeholder={textInputPlaceholder}
        />
      </PathwayModal.Body>
      <PathwayModal.Footer>
        <PathwayModal.SecondaryButton
          onPress={onSecondaryClick}
          testId="ConfirmationPopUpSecondaryButton"
        >
          {secondaryButtonText}
        </PathwayModal.SecondaryButton>
        <PathwayModal.PrimaryButton
          isDisabled={isDisabled}
          onPress={() => {
            primaryButtonHandler?.(textInputValue ?? clampedDefaultValue ?? '');
          }}
          testId="ConfirmationPopUpPrimaryButton"
          variant={primaryButtonVariant}
        >
          {primaryButtonText}
        </PathwayModal.PrimaryButton>
      </PathwayModal.Footer>
    </PathwayModal>
  );
};

export default InputDialog;
